import { StringUtils } from "Global/Base/Utils/StringUtils";
import { Localization } from "../../../../../OW.Remoting/Remoting/Localization";
import { Checkbox } from "../../../../UI.BaseControls/Checkbox";
import { Control } from "../../../../UI.BaseControls/Control";
import { IDropDownMenu } from "../../../../UI.Controls/DropDownMenu/DropDownInterfaces";
import { createElement, registerCustomElement } from "../../../../UI.ElementarControls/Utils/CustomElementUtils";
import { IHasFrameContext } from "../../Controls/ControlInterfaces";
import { FrameContext } from "../../UIManagerInterfaces";
import { FilterCombo } from "./FilterCombo";
import { FilterJsonResultSign, FilterTargetType, IFilterBlock, IFilterGroup, IFilterItem, IFilterItemDefinition, IFilterJsonDataAttr, IFilterJsonResult, IFilterValue, IMultiOperation, IOperation } from "./FilterInterfaces";
import { FilterItem } from "./FilterItem";
import { FilterMultiItem } from "./FilterMultiItem";
import { FilterRegistrations } from "./FilterRegistrations";
import { logger } from "./logger";

const TEMP_REMOVED_CLASS = "TemporarilyRemoved";

/**
 * Třída obsahující skupinu FilterItem
 */
export class FilterGroup extends Control implements IFilterGroup, IHasFrameContext {
    /**
     * Nadrazeny filterBlock
     */
    public parent: IFilterBlock;
    /**
     * Jméno atributu
     */
    public attrName: string;
    public displayFormat: string;
    private _sign: FilterJsonResultSign = "and";
    private _filterStructure: IFilterValue; // struktura filtru
    private _isUsed: boolean;
    private _filterListeners: any;
    private _canDelete: boolean;
    private _canFirstItemDelete: boolean;
    private _parentMenu: IDropDownMenu;

    private _locKey: string;
    private _locParams: string[];
    /** Kontext framu */
    public frameContext: FrameContext | undefined = undefined;

    /**
     * Zakáže smazání skupiny
     */
    public get canDelete(): boolean {
        return this._canDelete;
    }

    public set canDelete(v: boolean) {
        this._canDelete = v;

        this._removeElement.style.display = v ? "" : "none";
    }

    /**
     * Zakáže smazání první položky ve skupině
     */
    public get canFirstItemDelete(): boolean {
        return this._canFirstItemDelete;
    }

    public set canFirstItemDelete(v: boolean) {
        this._canFirstItemDelete = v;
        this.classList.toggle("FirstItemCantDelete", !v);
    }

    public get locKey(): string {
        return this._locKey;
    }
    public get locParams(): string[] {
        return this._locParams;
    }

    public get filterStructure(): IFilterValue {
        return this._filterStructure;
    }

    /**
     * Struktura filtru
     */
    public set filterStructure(value: IFilterValue) {
        this._filterStructure = value;
        if (value) {
            this.setAttribute("data-type", this._filterStructure.dataType);

            if (value.basicMultiOperation || value.multiOperation) {
                this._addConditionButton.visible = false;
            } else {
                this._addConditionButton.parseFromStructure(value);
            }
        }
    }

    public get operator(): FilterJsonResultSign {
        return this._sign;
    }

    public set operator(sign: FilterJsonResultSign) {
        if (sign != this._sign) {
            this._sign = sign;
            if (this.parent) this.parent.unsave();
        }
    }

    /**
     * Indikuje zda je filtrovací skupina použita
     */
    public override get enabled(): boolean {
        return this._isUsed;
    }

    public override set enabled(v: boolean) {
        this._isUsed = v;
        this._isUsedElement.checked = v;
        this.classList.toggle("notUsed", !this._isUsed);
    }

    /**
     * Vrací FilterItemy BEZ těch dočasně odstraněných, viz getter na this._temporarilyRemovedItems.
     */
    public get items(): IFilterItem[] {
        return Array.from(this.querySelectorAll(`.FilterItem:not(.${TEMP_REMOVED_CLASS})`)) as IFilterItem[];
    }

    /**
     * Vrátí FilterItemy, které jsou označeny za dočasně odstraněné (getter na this.items je nevrací). Využívá se to u smazání celé FilterGroup (pomocí ikony popelnice), protože tato akce umožňuje filtr po určitý časový interval obnovit.
     */
    private get _temporarilyRemovedItems(): IFilterItem[] {
        return Array.from(this.querySelectorAll(`.FilterItem.${TEMP_REMOVED_CLASS}`)) as IFilterItem[];
    }

    /**
     * Informace o tom, zda je element prave ve fazi odebirání (zobrazena moznost undo)
     */
    public get isRemoving(): boolean {
        return this._isRemoving;
    }

    public get parentMenu(): IDropDownMenu {
        return this._parentMenu;
    }

    public set parentMenu(menu: IDropDownMenu) {
        this._parentMenu = menu;
        this._addConditionButton.parentMenu = menu;
    }

    //#region Inner elements
    private _hideArrowElement: HTMLElement; // sipka na skrytí skupiny
    private _nameElement: HTMLElement; // element s nazvem skupiny
    private _contentElement: HTMLElement; // element s obsahem skupiny
    private _isUsedElement: Checkbox; // checkbox element pro IsUsed
    private _removeElement: HTMLElement; // remove button
    private _undoElement: HTMLElement; // undo button
    private _undoInterval: any = null;
    private _undoIntervalCounter: number = 0;
    private _isRemoving: boolean;
    private _addConditionButton: FilterCombo; // Combobox na pridani další filterItem
    //#endregion Inner elements

    /**
     * Přidá jednu položku filtru
     * @param item Položka k vložení
     * @param insertBefore Položka, místo které má dojít ke vložení
     */
    protected _addItem(item: IFilterItem, insertBefore?: IFilterItem): void {
        item.group = this;
        item.parent = this.parent;

        if (insertBefore) {
            this._contentElement.insertBefore(item as HTMLElement, insertBefore);

            return;
        }

        this._contentElement.appendChild(item as HTMLElement);
    }

    /**
     * Nastavením css třídy (TemporarilyRemoved) odstraní všechny své FilterItemy z this.items, viz získávání této property. Volá se při odstraňování celé FilterGroup, což jde po určitý časový úsek vrátit a proto se to řeší přes css třídu a ne přes funkci remove().
     */
    private _temporarilyRemoveAllItems(): void {
        for (const item of this.items) {
            item.classList.add(TEMP_REMOVED_CLASS);
        }
    }

    /**
     * Vrátí výsledek filtru
     * @param isForExport Vrácený objekt, nebude mít definované property, které jsou defaultně true. (např. enabled)
     */
    public getResult(isForExport: boolean = false, filterTargetType: FilterTargetType): IFilterJsonDataAttr {
        const items = this.items;
        const arr: Array<IFilterJsonResult> = [];

        for (const item of items) {
            const result = item.getResult(isForExport, filterTargetType);

            arr.push(result);
        }
        return {
            operator: isForExport && this.operator == "and" ? undefined : this.operator,
            attrName: this.attrName,
            conditions: arr,
            enabled: isForExport && this.enabled ? undefined : this.enabled,
            displayFormat: this.displayFormat,
        };
    }

    protected override _constructor(): void {
        super._constructor();

        this._filterListeners = {};
        this.classList.add("FilterGroup", "Opened");
        this._isRemoving = false;

        this._canDelete = true;
        this._canFirstItemDelete = true;

        this._isUsed = true;
        //#region Tvorba základních elementu
        const top = createElement({ classes: "top" });
        const nameDiv = createElement({ classes: "nameDiv" });
        this._contentElement = createElement({ classes: "content" });
        this._hideArrowElement = createElement({ classes: "arrow" });

        this._nameElement = createElement({ classes: "name" });

        this._addConditionButton = new FilterCombo();
        this._addConditionButton.classList.add("AddCondition");
        this._addConditionButton.defaultLocKey = "GUI-598877"; // Přidat podmínku
        this._addConditionButton.canUseFilter = false;
        this._addConditionButton.canSelectItem = false;

        this._addConditionButton.afterAttach(() => {
            this._addConditionButton.registerEventListener("filterExecuteRequest", () => this.parent.executeRequest = true);
        });

        const btnContent = createElement({ classes: "btn_bar" });
        this._isUsedElement = new Checkbox();
        this._isUsedElement.classList.add("is_used", "is-dot");
        Localization.addLocDescData(this._isUsedElement, "GUI-668892", "title");//Deaktivovat filtr sloupce
        this._removeElement = createElement({ classes: ["icon", "remove", "editorial-trash"] });
        Localization.addLocDescData(this._removeElement, "GUI-649338", "title");//odebrat
        this._undoElement = createElement({ classes: ["icon", "undo", "ui-undo"] });
        Localization.addLocDescData(this._undoElement, "GUI-615976", "title");//Vrátit změny

        this._isUsedElement.isNullable = false;
        this._isUsedElement.standardAction = true;
        //#endregion Tvorba základních elementu

        this._isUsedElement.checked = true;

        //#region Append elementů do hlavního elementů
        nameDiv.appendChild(this._hideArrowElement);
        nameDiv.appendChild(this._nameElement);
        top.appendChild(nameDiv);
        Localization.addLocDescData(nameDiv, "GUI-668898", "title");//Sbalit fitr sloupce
        this.appendChild(top);
        this.appendChild(this._contentElement);
        this.appendChild(this._addConditionButton);

        btnContent.appendChild(this._isUsedElement as HTMLElement);
        btnContent.appendChild(this._removeElement);
        btnContent.appendChild(this._undoElement);

        top.appendChild(btnContent);
        //#endregion Append elementů do hlavního elementů
    }

    /**
     * Vytvoří a přidá do skupiny jednu položku filtru podle definice
     * @param definition Definice položky filtru
     * @param itemToReplace Položka, která má být nahrazena
     */
    public createFilterItem(definition: IFilterItemDefinition, itemToReplace?: IFilterItem): void {
        if (definition.operation instanceof IMultiOperation) {
            const item = new FilterMultiItem();
            item.parentMenu = this.parentMenu;
            item.currentSign = definition.operator;
            item.isUsed = definition.isUsed;

            this._addItem(item, itemToReplace);
            item.loadStruc(definition.operation, definition.values);

            return;
        }

        const item = new FilterItem();
        item.frameContext = this.frameContext;
        item.parentMenu = this.parentMenu;
        item.currentSign = definition.operator ? definition.operator : "or";
        item.isUsed = definition.isUsed;
        item.dataType = this._filterStructure.dataType;

        this._addItem(item, itemToReplace);
        item.loadFromStructure(definition.operation, definition.values);

        if (definition.operation.id === "extended") {
            this._registerExtendedFilterListener(item);
        }

        item.focus();
    }

    /**
     * Zaregistruje listener pro přepisování filtru skrze event z ExtendedFilterEdit
     * @param item Položka, u které má dojít k přepsání
     */
    private _registerExtendedFilterListener(item: FilterItem): void {
        const operation = FilterRegistrations.getDefaultOperation(this._filterStructure.dataType);
        item.registerEventListener("ExtendedFilterParsed", (e: CustomEvent) => {
            let signChanged = false;
            for (const filter of e.detail.filters) {
                this.createFilterItem({
                    operation: operation,
                    values: filter ? { ["input1"]: filter.value } : undefined,
                    operator: (signChanged) ? filter.operator : item.currentSign,
                    isUsed: true,
                }, item);
                signChanged = true;
            }

            if (item) {
                item.remove();
            }

            this.parent.unsave();
        });
    }
    public setLocKey(locKey: string, locParams?: string[]): void {
        this._locKey = locKey;
        this._locParams = locParams;
        if (this._locKey) {
            Localization.getTextPromiseWithParams(this._locKey, this._locParams).then((locItem) => {
                this._nameElement.innerHTML = `<span>${StringUtils.stringToHTMLText(locItem)}</span>`;
            }).catch((e) => logger.error(e));
        } else
            this._nameElement.innerHTML = "";
    }

    // You can also define the other lifecycle methods.
    protected override connectedCallback(): void {
        super.connectedCallback();

        // Zpracování události s komba na přidání podmínky
        this._addConditionButton.registerEventListener("ItemSelected", (e: CustomEvent) => {
            // vytvoří novou položku a přidá do stránky
            const operation: IOperation = e.detail.operation;
            this.createFilterItem({ operation: operation, isUsed: true });
        });

        this._contentElement.classList.toggle("Disabled", !this._isUsed);

        // checkbox o použití filtru
        this._isUsedElement.registerEventListener("change", () => {
            this._isUsed = this._isUsedElement.checked;

            this._contentElement.classList.toggle("Disabled", !this._isUsed);
            if (this._isUsed)
                Localization.addLocDescData(this._isUsedElement, "GUI-668892", "title"); //Deaktivovat filtr sloupce
            else
                Localization.addLocDescData(this._isUsedElement, "GUI-668894", "title"); //Aktivovat filtr sloupce

            for (const item of this.items) {
                item.isUsed = this._isUsed;
            }
            this.parent.unsave();
        });

        // tlacitko na smazani skupiny filtru
        this._removeElement.addEventListener("click", this._filterListeners["removeElement"] = () => {
            if (!this._canDelete)
                return;

            this.classList.add(TEMP_REMOVED_CLASS);
            this._isRemoving = true;
            const lastCount = this.parent.executeCount;

            const f_clearInterval = () => {
                clearInterval(this._undoInterval);
                this._undoIntervalCounter = 0;
                this._undoInterval = null;
            };

            if (this._undoInterval) f_clearInterval();

            this._temporarilyRemoveAllItems();
            this.parent.unsave();
            this.parent.filterGroupRemoved.trigger(this, { temporarily: true });
            this._undoInterval = self.setInterval(() => {
                if (this.parent.executeCount != lastCount || this._undoIntervalCounter++ >= 50) {
                    f_clearInterval();

                    this.classList.remove(TEMP_REMOVED_CLASS);
                    this.remove();
                }
            }, 100);
        });

        this._hideArrowElement.addEventListener("click", this._filterListeners["hideBlock"] = () => {
            this.classList.toggle("Opened");
        });

        this._nameElement.addEventListener("click", this._filterListeners["hideBlock2"] = () => {
            this.classList.toggle("Opened");
        });

        this._undoElement.addEventListener("click", this._filterListeners["undoClick"] = () => {
            if (this._undoInterval)
                clearInterval(this._undoInterval);

            this.classList.remove(TEMP_REMOVED_CLASS);
            for (const removedItem of this._temporarilyRemovedItems) {
                removedItem.classList.remove(TEMP_REMOVED_CLASS);
            }

            this.parent.filterGroupRestored.trigger(this, null);
            this._isRemoving = false;
            this.parent.unsave();
        });

        this.registerEventListener("ExtendedFilterCreated", this._filterListeners["ExtendedFilterCreated"] = (e: CustomEvent) => {
            this._registerExtendedFilterListener(e.detail.filter);
        });
    }

    protected override disconnectedCallback(): void {
        super.disconnectedCallback();

        this._hideArrowElement.removeEventListener("click", this._filterListeners["hideBlock"]);
        this._removeElement.removeEventListener("click", this._filterListeners["removeElement"]);
        this._nameElement.removeEventListener("click", this._filterListeners["hideBlock2"]);
        this._undoElement.removeEventListener("click", this._filterListeners["undoClick"]);
        this.removeEventListener("ExtendedFilterCreated", this._filterListeners["ExtendedFilterCreated"]);

        if (this.parent.isConnected && !this.parent.groups.length) { // Pokud je parent stále v HTML DOMu, ale po smazání této FilterGroup neobsahuje žádnou další, je potřeba vytvořit novou
            this.parent.filterGroupRemoved.trigger(this, { temporarily: false });
        }
    }
}

registerCustomElement("tsc-FilterGroup", FilterGroup);
